import { MODERATION_TEXTS, type Generation } from '@trendator/shared';
import { useEffect, useState } from 'react';
import { api } from '../api';
import { MusicIcon, PlayFill, VideoIcon } from '../components/icons';
import { plural, rub } from '../format';

const FILTERS = ['Все', 'Фото', 'Видео', 'Песни'] as const;

function ago(iso: string): string {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.round(h / 24);
  return d === 1 ? 'вчера' : `${d} дн назад`;
}

const FILTER_KIND: Record<string, Generation['kind']> = { Фото: 'photo', Видео: 'video', Песни: 'song' };
const inFilter = (filter: string, kind: Generation['kind']) => filter === 'Все' || FILTER_KIND[filter] === kind || (filter === 'Фото' && kind === 'stickers');

function summary(g: Generation): string {
  const n = g.results.length;
  if (g.kind === 'video') return n > 1 ? `${n} видео` : 'Видео';
  if (g.kind === 'song') return n > 1 ? `${n} ${plural(n, 'вариант', 'варианта', 'вариантов')} песни` : 'Песня';
  if (g.kind === 'stickers') return `Стикерпак · ${n}`;
  return `${n} фото`;
}

const RUNNING = { photo: 'Делаем фото…', video: 'Снимаем видео…', song: 'Пишем песню…', stickers: 'Рисуем стикеры…' } as const;

/** История — docs/design/History.dc.html. */
export function History({ onOpen }: { onOpen: (id: string) => void }) {
  const [items, setItems] = useState<Generation[] | null>(null);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('Все');
  const [error, setError] = useState(false);

  useEffect(() => {
    let stopped = false;
    const load = () =>
      api
        .generations()
        .then((list) => {
          if (stopped) return;
          setItems(list);
          // Есть незаконченные — обновим через пару секунд.
          if (list.some((g) => g.status === 'queued' || g.status === 'running')) setTimeout(load, 3000);
        })
        .catch(() => !stopped && setError(true));
    void load();
    return () => {
      stopped = true;
    };
  }, []);

  const shown = (items ?? []).filter((g) => inFilter(filter, g.kind));

  return (
    <div className="history">
      <header className="history__header">
        <h1 className="page-title">История</h1>
        <span className="history__hint">храним 30 дней</span>
      </header>
      <div className="segments" role="group" aria-label="Фильтр" style={{ margin: '12px 16px 0' }}>
        {FILTERS.map((f) => (
          <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {f}
          </button>
        ))}
      </div>

      {error && <p className="error-text" style={{ marginTop: 24 }}>Не получилось загрузить историю.</p>}
      {items && shown.length === 0 && (
        <div className="center" style={{ minHeight: 'calc(40 * var(--dvh))' }}>
          <p>Пока пусто. Первая генерация — бесплатно.</p>
        </div>
      )}

      <ul className="glass-list history__list">
        {shown.map((g, i) => {
          const running = g.status === 'queued' || g.status === 'running';
          const meta = running
            ? RUNNING[g.kind]
            : g.status === 'failed'
              ? `${g.error === MODERATION_TEXTS.output_blocked ? 'Не прошло модерацию' : 'Модель не ответила'} · ${g.free ? 'попытка вернулась' : `${rub(g.price)} вернули`}`
              : `${summary(g)}${g.duo ? ` · дуо: ${g.duo.friendName}` : ''} · ${ago(g.createdAt)}`;
          const first = g.results[0];
          const thumb = first ? (first.type === 'video' || first.type === 'audio' ? first.coverUrl : first.url) : null;
          return (
            <li key={g.id}>
              {i > 0 && <div className="glass-list__sep" style={{ marginLeft: 84 }} />}
              <button type="button" className="history__item" onClick={() => onOpen(g.id)}>
                <span className="history__thumb">
                  {thumb ? (
                    <img src={thumb} alt="" />
                  ) : first ? (
                    <span className="history__kind">{first.type === 'audio' ? <MusicIcon /> : <VideoIcon />}</span>
                  ) : (
                    <span className={running ? 'spinner spinner--sm' : ''} />
                  )}
                  {first?.type === 'video' && thumb ? (
                    <span className="history__play">
                      <PlayFill />
                    </span>
                  ) : null}
                </span>
                <span className="history__text">
                  <span className="history__title">{g.title}</span>
                  <span className="history__meta">{meta}</span>
                </span>
                {!running && (
                  <span className={`history__badge${g.status === 'failed' ? ' history__badge--refund' : ''}`}>
                    {g.status === 'failed' ? 'Возврат' : 'Готово'}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
